// Sidebar badge stats + imperative refresh.
// Provider drží `stats` z `/api/dashboard/stats`, pollová 30s a exponuje
// `refetch()` pro stránky, aby po mutaci mohly okamžitě aktualizovat
// sidebar — bez čekání na polling interval.
//
// Paralelně subscribe na user-specific notification topic.
// Backend posílá `notification.new` po každé business akci (assign,
// approval, atd. — viz notification_triggers.py). Při příchozím eventu
// hned refetchneme stats → sidebar badges se update bez čekání na polling.
import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import api from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { useWebSocket } from '@/hooks/useWebSocket';

const StatsContext = createContext({ stats: {}, refetch: () => {} });

const POLL_INTERVAL_MS = 30_000;

export function StatsProvider({ children }) {
  const [stats, setStats] = useState({});
  const { user } = useAuth();
  const timerRef = useRef(null);

  const refetch = useCallback(async () => {
    try {
      const r = await api.get('/dashboard/stats');
      setStats(r.data || {});
    } catch {
      // silent — polling zkusí znovu
    }
  }, []);

  useEffect(() => {
    if (!user) {
      setStats({});
      return;
    }
    refetch();
    timerRef.current = setInterval(refetch, POLL_INTERVAL_MS);
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [user, refetch]);

  // WS-driven invalidace stats. Backend posílá notification
  // event po každé business akci, která může ovlivnit sidebar counters
  // (assignment, completion, approval, rejection). Při příchozím eventu
  // refetchneme stats okamžitě (mimo 30s polling cyklus).
  useWebSocket(
    user?.id ? `user:${user.id}:notifications` : null,
    (event) => {
      if (event.event === 'notification.new') {
        refetch();
      }
    },
    !!user?.id
  );

  return (
    <StatsContext.Provider value={{ stats, refetch }}>
      {children}
    </StatsContext.Provider>
  );
}

export const useStats = () => useContext(StatsContext);
